// RB-MY01-PalmGarden.web/src/App.tsx
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';

// Import public components
import { 
  HomePage, 
  PlantsSearchPage, 
  PlantDetailPage, 
  PublicLayout 
} from './PublicRoute';

// Import admin components
import { 
  AdminPanel, 
  Profile, 
  Dashboard, 
  ProtectedRoute,
  AdminLoginPage,
  AdminLoginForm,
  AdminRoute,
  ImageUploadTest,
  ImageUploader,
  AdminDashboard,
  AddPlantFormWrapper,
  AdminLayout,
  PlantManagement
} from './AdminRoute';

// Import existing components
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Plants from './pages/Plants';
import Unauthorized from './pages/Unauthorized';
import PlantForm from './components/PlantForm';
import NotFound from './pages/NotFound';

// Import styles
import './styles/header.css';
import './styles/dashboard.css';
import './styles/navigation.css';
import './styles/plants.css';
import './styles/footer.css';
import './styles/plantForm.css';

const App: React.FC = () => {
  const location = useLocation();
  
  // Detect which domain we're on
  const isAdminDomain = window.location.hostname === 'admin.palmgardenrs.com';
  const isPublicDomain = window.location.hostname === 'palmgardenrs.com';
  
  // Check if we're on a login or auth page
  const isAuthPage = ['/login', '/signup', '/unauthorized', '/admin/login'].includes(location.pathname);
  
  // Check if we're on an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Check if we're on a public route
  const isPublicRoute = ['/', '/plants'].includes(location.pathname) || 
                       location.pathname.startsWith('/plants/');
  
  // Determine which layout to show based on domain
  const shouldShowPublicLayout = isPublicDomain && isPublicRoute;
  const shouldShowAdminLayout = isAdminDomain || isAdminRoute;
  
  return (
    <AuthProvider>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh' 
      }}>
        {/* Show public layout for public domain */}
        {shouldShowPublicLayout ? (
          <PublicLayout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/plants" element={<PlantsSearchPage />} />
              <Route path="/plants/:id" element={<PlantDetailPage />} />
            </Routes>
          </PublicLayout>
        ) : (
          <>
            {/* Show admin header for admin routes */}
            {!isAuthPage && shouldShowAdminLayout && <Header />}
            
            <Box sx={{ 
              flexGrow: 1, 
              width: '100%', 
              maxWidth: 1200, 
              mx: 'auto', 
              px: 2 
            }}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                
                {/* Admin root route - redirect to admin dashboard */}
                <Route path="/" element={
                  isAdminDomain ? (
                    <Navigate to="/admin/dashboard" replace />
                  ) : (
                    <Navigate to="/" replace />
                  )
                } />
                
                {/* Admin routes with layout */}
                <Route path="/admin" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <AdminPanel />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/login" element={<AdminLoginPage />} />
                
                <Route path="/admin/dashboard" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/plants" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <PlantManagement />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/plants/add" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <AddPlantFormWrapper />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/upload-test" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <ImageUploadTest />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                {/* Legacy routes for backward compatibility */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <Dashboard />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <Profile />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/plants" element={
                  <ProtectedRoute>
                    <Plants />
                  </ProtectedRoute>
                } />
                
                <Route path="/plants/:id" element={
                  <ProtectedRoute>
                    <PlantDetailPage />
                  </ProtectedRoute>
                } />
                
                <Route path="/plants/add" element={
                  <ProtectedRoute>
                    <PlantForm />
                  </ProtectedRoute>
                } />
                
                <Route path="/plants/edit/:id" element={
                  <ProtectedRoute>
                    <PlantForm />
                  </ProtectedRoute>
                } />
                
                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Box>
            
            {!isAuthPage && shouldShowAdminLayout && <Footer />}
          </>
        )}
      </Box>
    </AuthProvider>
  );
};

export default App;
